import { useState } from "react";
import { toast } from "sonner";
import { uploadLogo, fileUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { UploadCloud, FileCheck, X, Type } from "lucide-react";

const TEXT_COLORS = [
  { name: "Noir", hex: "#111111" },
  { name: "Blanc", hex: "#FFFFFF" },
  { name: "Bleu", hex: "#0044FF" },
  { name: "Rouge", hex: "#B23A48" },
  { name: "Or", hex: "#C9A227" },
];

export default function Customizer({ side }) {
  const { customization, updateSide } = useCart();
  const s = customization[side];
  const [uploading, setUploading] = useState(false);
  const label = side === "front" ? "devant" : "dos";

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadLogo(fd);
      updateSide(side, { logo: { id: res.id, filename: res.filename, url: fileUrl(res.url) } });
      toast.success(`Logo ${label} importé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'import");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Logo */}
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#555] mb-2">Logo {label}</p>
        {s.logo ? (
          <div className="flex items-center justify-between gap-3 border border-[#e5e5e5] bg-white px-3 py-2">
            <span className="flex items-center gap-2 min-w-0">
              <FileCheck className="w-4 h-4 text-[#0044ff] shrink-0" />
              <span className="truncate text-sm">{s.logo.filename}</span>
            </span>
            <button onClick={() => updateSide(side, { logo: null })} data-testid={`logo-remove-${side}`} className="text-[#999] hover:text-red-500 shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label className="flex items-center gap-2 border border-black px-4 py-3 font-bold uppercase tracking-widest text-xs cursor-pointer hover:bg-black hover:text-white transition-colors w-fit">
            <UploadCloud className="w-4 h-4" />
            {uploading ? "Import…" : `Importer le logo ${label}`}
            <input type="file" data-testid={`logo-input-${side}`} accept=".png,.jpg,.jpeg,.svg,.pdf,.webp" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
          </label>
        )}
        {s.logo && (
          <div className="flex items-center gap-3 mt-3">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#555]">Taille</span>
            <input
              type="range" min="8" max="65" value={s.logoPlacement.scale}
              data-testid={`logo-size-${side}`}
              onChange={(e) => updateSide(side, { logoPlacement: { ...s.logoPlacement, scale: parseInt(e.target.value) } })}
              className="flex-1 accent-[#0044ff]"
            />
          </div>
        )}
      </div>

      {/* Text */}
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#555] mb-2">Texte {label}</p>
        <div className="relative">
          <Type className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <input
            type="text"
            data-testid={`text-input-${side}`}
            value={s.text}
            maxLength={24}
            placeholder="Ex : votre slogan ou nom d'équipe"
            onChange={(e) => updateSide(side, { text: e.target.value })}
            className="w-full border border-[#ccc] bg-white pl-9 pr-4 py-3 outline-none focus:border-black transition-colors text-sm"
          />
        </div>
        {s.text && (
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-3">
              <span className="font-mono text-[11px] uppercase tracking-widest text-[#555]">Taille</span>
              <input
                type="range" min="3" max="16" value={s.textPlacement.size}
                data-testid={`text-size-${side}`}
                onChange={(e) => updateSide(side, { textPlacement: { ...s.textPlacement, size: parseInt(e.target.value) } })}
                className="flex-1 accent-[#0044ff]"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[11px] uppercase tracking-widest text-[#555]">Couleur</span>
              <div className="flex gap-2">
                {TEXT_COLORS.map((c) => (
                  <button
                    key={c.hex}
                    data-testid={`textcolor-${side}-${c.name}`}
                    onClick={() => updateSide(side, { textColor: c.hex })}
                    title={c.name}
                    className={`w-6 h-6 rounded-full border transition-all ${s.textColor === c.hex ? "ring-2 ring-offset-1 ring-[#0044ff] border-transparent" : "border-[#ccc] hover:scale-110"}`}
                    style={{ background: c.hex }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
